import { PlanStep } from './PlanStep';
import { PlanDomain } from './PlanDomain';
import { PlanStepPage } from './PlanStepPage';

export class PlanCoordinate {
  private static readonly _planDomains: PlanCoordinate[] = [];
  private static readonly _planSteps: PlanCoordinate[] = [];
  private static readonly _planStepPages: PlanCoordinate[] = [];

  private static get planDomains() {
    this.initializeIfNeeded();
    return this._planDomains;
  }

  private static get planSteps() {
    this.initializeIfNeeded();
    return this._planSteps;
  }

  private static get planStepPages() {
    this.initializeIfNeeded();
    return this._planStepPages;
  }

  private static initializeIfNeeded() {
    if (!this._planDomains.length || !this._planSteps.length || !this._planStepPages.length) {
      this.initialize();
    }
  }

  private static initialize() {
    this._planDomains.length = 0;
    this._planSteps.length = 0;
    this._planStepPages.length = 0;

    this._planDomains.push(PlanCoordinate.create(PlanDomain.ENVISIONING));
    this._planSteps.push(PlanCoordinate.create(PlanDomain.ENVISIONING, PlanStep.DETERMINE_CURRENT_REALITY));
    this._planSteps.push(PlanCoordinate.create(PlanDomain.ENVISIONING, PlanStep.EXPLORE_THE_FUTURE));
    this._planSteps.push(PlanCoordinate.create(PlanDomain.ENVISIONING, PlanStep.SYNTHESIZE_RESULTS));

    this._planDomains.push(PlanCoordinate.create(PlanDomain.PLANNING));
    this._planSteps.push(PlanCoordinate.create(PlanDomain.PLANNING, PlanStep.CREATE_OBJECTIVES_AND_CRITICAL_INITIATIVES));
    this._planSteps.push(PlanCoordinate.create(PlanDomain.PLANNING, PlanStep.IDENTIFY_OUTCOMES_AND_KEY_MEASURES));
    this._planSteps.push(PlanCoordinate.create(PlanDomain.PLANNING, PlanStep.DEVELOP_STRATEGY_MAP));
    this._planSteps.push(PlanCoordinate.create(PlanDomain.PLANNING, PlanStep.DEVELOP_THE_ANNUAL_PLAN));

    this._planDomains.push(PlanCoordinate.create(PlanDomain.IMPLEMENTING));
    this._planSteps.push(PlanCoordinate.create(PlanDomain.IMPLEMENTING, PlanStep.DESIGN_THE_IMPLEMENTATION_PLAN));
    this._planSteps.push(PlanCoordinate.create(PlanDomain.IMPLEMENTING, PlanStep.ASSIGN_AND_COMMUNICATE_RESPONSIBILITIES));
    this._planSteps.push(PlanCoordinate.create(PlanDomain.IMPLEMENTING, PlanStep.ENACT_AND_MONITOR_PROGRESS));

    this._planDomains.push(PlanCoordinate.create(PlanDomain.EVALUATING));
    this._planSteps.push(PlanCoordinate.create(PlanDomain.EVALUATING, PlanStep.DETERMINE_IMPACT_OF_CRITICAL_INITIATIVES));
    this._planSteps.push(PlanCoordinate.create(PlanDomain.EVALUATING, PlanStep.JUDGE_SUCCESS_OF_ANNUAL_PLAN));
    this._planSteps.push(PlanCoordinate.create(PlanDomain.EVALUATING, PlanStep.ADDRESS_IMPLICATIONS_FOR_FUTURE_WORK));

    this._planDomains.push(PlanCoordinate.create(PlanDomain.OUTPUTS));
    this._planSteps.push(PlanCoordinate.create(PlanDomain.OUTPUTS, PlanStep.OUTPUTS));
    this._planStepPages.push(PlanCoordinate.create(PlanDomain.OUTPUTS, PlanStep.OUTPUTS, PlanStepPage.StrategyMap));
    this._planStepPages.push(PlanCoordinate.create(PlanDomain.OUTPUTS, PlanStep.OUTPUTS, PlanStepPage.AnnualPlan));
    this._planStepPages.push(PlanCoordinate.create(PlanDomain.OUTPUTS, PlanStep.OUTPUTS, PlanStepPage.ImplementationPlan));
    this._planStepPages.push(PlanCoordinate.create(PlanDomain.OUTPUTS, PlanStep.OUTPUTS, PlanStepPage.TheoryOfAction));
  }

  private constructor(readonly planDomain: PlanDomain, readonly planStep: PlanStep, readonly planStepPage: PlanStepPage) { }

  private static create(planDomain: PlanDomain, planStep?: PlanStep, planStepPage?: PlanStepPage) {
    return new PlanCoordinate(planDomain, planStep, planStepPage);
  }

  // ==========================================================================

  static domains(): PlanCoordinate[] {
    return this.planDomains;
  }

  static steps(planCoordinate: PlanCoordinate): PlanCoordinate[] {
    return this.planSteps.filter(s => s.planDomain === planCoordinate.planDomain);
  }

  static stepPages(planCoordinate: PlanCoordinate): PlanCoordinate[] {
    return this.planStepPages.filter(p => (p.planDomain === planCoordinate.planDomain) && (p.planStep === planCoordinate.planStep));
  }

  // ==========================================================================

  static domainBy(planDomain: PlanDomain): PlanCoordinate {
    return this.planDomains.find(d => d.planDomain === planDomain);
  }

  static stepBy(planStep: PlanStep): PlanCoordinate {
    return this.planSteps.find(s => s.planStep === planStep);
  }

  static stepPageBy(planStepPage: PlanStepPage): PlanCoordinate {
    return this.planStepPages.find(s => s.planStepPage === planStepPage);
  }

  // ==========================================================================

  static stepsBy(planDomain: PlanDomain): PlanCoordinate[] {
    return this.planSteps.filter(s => s.planDomain === planDomain);
  }

  static stepPagesBy(planStep: PlanStep): PlanCoordinate[] {
    return this.planSteps.filter(s => s.planStep === planStep);
  }

  // ==========================================================================

  isEqual(planCoordinate: PlanCoordinate): boolean {
    if (!planCoordinate) {
      return false;
    } else if (planCoordinate === this) {
      return true;
    } else {
      return this.planDomain === planCoordinate.planDomain &&
        this.planStep === planCoordinate.planStep &&
        this.planStepPage === planCoordinate.planStepPage;
    }
  }

  get hasPlanDomain() {
    return this.planDomain !== null && this.planDomain !== undefined;
  }

  get hasPlanStep() {
    return this.planStep !== null && this.planStep !== undefined;
  }

  get hasPlanStepPage() {
    return this.planStepPage !== null && this.planStepPage !== undefined;
  }

  toString(): string {
    if (this.hasPlanDomain && this.hasPlanStep && this.hasPlanStepPage) {
      return `PlanCoordinate[${PlanDomain[this.planDomain]}][${PlanStep[this.planStep]}][${PlanStepPage[this.planStepPage]}]`;
    } else if (this.hasPlanDomain && this.hasPlanStep) {
      return `PlanCoordinate[${PlanDomain[this.planDomain]}][${PlanStep[this.planStep]}]`;
    } else if (this.hasPlanDomain) {
      return `PlanCoordinate[${PlanDomain[this.planDomain]}]`;
    } else {
      return 'PlanCoordinate[]';
    }
  }

  getNextStepCoordinate(accept: (planCoordinate: PlanCoordinate) => boolean): PlanCoordinate {
    for (let i = PlanCoordinate._planSteps.findIndex(it => it.planStep === this.planStep) + 1; i < PlanCoordinate._planSteps.length; i++) {
      const next = PlanCoordinate._planSteps[i];
      if (accept(next)) {
        return next;
      }
    }
    return this;
  }

  getPreviousStepCoordinate(accept: (planCoordinate: PlanCoordinate) => boolean): PlanCoordinate {
    for (let i = PlanCoordinate._planSteps.findIndex(it => it.planStep === this.planStep) - 1; i >= 0; i--) {
      const previous = PlanCoordinate._planSteps[i];
      if (accept(previous)) {
        return previous;
      }
    }
    return this;
  }
}
